import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Search } from 'lucide-react';
import clsx from 'clsx';
import { get } from '../lib/api';
import { Loading } from '../ui';

/**
 * The account, laid out the way Google Ads lays it out.
 *
 * Deliberately familiar: tabs across the top, the totals row above the data
 * rather than buried at the bottom, numbers right-aligned, click a heading to
 * sort. Somebody who works in Google Ads all day should not have to learn
 * anything to read this, and the audit's claims are ten times more persuasive
 * when the row behind one is two clicks away.
 */

type Column = {
  key: string; label: string;
  type: 'text' | 'number' | 'money' | 'percent' | 'duration';
  total?: boolean; wide?: boolean;
};

type Result = {
  kind: string; label: string; columns: Column[];
  rows: Record<string, unknown>[];
  total: number; totals: Record<string, number>;
  sort: string; dir: 'asc' | 'desc';
};

const TABS = [
  { kind: 'campaigns', label: 'Campaigns' },
  { kind: 'search_terms', label: 'Search terms' },
  { kind: 'keywords', label: 'Keywords' },
  { kind: 'ads', label: 'Ads' },
  { kind: 'conversion_actions', label: 'Conversions' },
  { kind: 'hourly', label: 'Hour of day' },
] as const;

const PAGE = 100;

function fmt(value: unknown, type: Column['type'], currency: string): string {
  if (value == null || value === '') return '—';
  switch (type) {
    case 'money':
      return new Intl.NumberFormat('en-IN', {
        style: 'currency', currency, maximumFractionDigits: 0,
      }).format(Number(value));
    case 'number': {
      const n = Number(value);
      // Two decimals only when there actually are any: "4 conversions" reads
      // better than "4.00", and "4.25" must not become "4".
      return Number.isInteger(n) ? n.toLocaleString('en-IN') : n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
    }
    case 'percent':
      return `${(Number(value) * 100).toFixed(1)}%`;
    default:
      if (typeof value === 'boolean') return value ? 'Yes' : '';
      return String(value);
  }
}

/** An hour is stored as a number and read as a time of day. */
const hourLabel = (h: unknown) => {
  const n = Number(h);
  if (!Number.isFinite(n)) return String(h ?? '—');
  const clock = (x: number) => `${((x + 11) % 12) + 1}${x < 12 ? 'am' : 'pm'}`;
  return `${clock(n)} – ${clock(n + 1)}`;
};

export function DataTable({ accountId, currency }: { accountId: string; currency: string }) {
  const [kind, setKind] = useState<string>('campaigns');
  const [sort, setSort] = useState<string | undefined>();
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);

  const query = useQuery({
    queryKey: ['rows', accountId, kind, sort, dir, q, page],
    queryFn: () => get<Result>(
      `/v1/accounts/${accountId}/rows/${kind}?` + new URLSearchParams({
        ...(sort ? { sort, dir } : {}),
        ...(q ? { q } : {}),
        limit: String(PAGE),
        offset: String(page * PAGE),
      }),
    ),
    // Keeps the old rows on screen while the next page loads, so sorting a
    // big table does not flash an empty one.
    placeholderData: keepPreviousData,
  });

  const d = query.data;
  const pages = d ? Math.ceil(d.total / PAGE) : 0;

  function clickHeader(key: string) {
    if (sort === key) setDir(dir === 'desc' ? 'asc' : 'desc');
    else { setSort(key); setDir('desc'); }
    setPage(0);
  }

  return (
    <div className="rounded-card border border-bd bg-bg2">
      <div className="flex gap-1 overflow-x-auto border-b border-bd px-2 pt-2">
        {TABS.map((t) => (
          <button
            key={t.kind}
            onClick={() => { setKind(t.kind); setSort(undefined); setDir('desc'); setQ(''); setPage(0); }}
            className={clsx('whitespace-nowrap rounded-t-lg border-b-2 px-3 py-2 text-sm',
              kind === t.kind ? 'border-ink font-medium text-t1' : 'border-transparent text-t2 hover:text-t1')}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-t3" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            placeholder="Search"
            className="h-8 w-56 rounded-btn border border-bd bg-bg pl-8 pr-3 text-sm outline-none placeholder:text-t3 focus:border-bd2"
          />
        </div>
        <span className="num text-xs text-t3">
          {d ? `${d.total.toLocaleString('en-IN')} rows` : ''}
        </span>
        {pages > 1 && (
          <div className="ml-auto flex items-center gap-2 text-xs">
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}
              className="rounded-btn border border-bd px-2 py-1 disabled:opacity-40">Back</button>
            <span className="num text-t3">{page + 1} / {pages}</span>
            <button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}
              className="rounded-btn border border-bd px-2 py-1 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>

      {query.isLoading || !d ? (
        <div className="px-4 pb-6"><Loading /></div>
      ) : !d.rows.length ? (
        <p className="px-4 pb-6 text-sm text-t3">
          {q ? 'Nothing matches that.' : 'Nothing imported for this table yet — pull from Google, or drop the report in.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg3 text-left text-xs font-medium text-t2">
              <tr>
                {d.columns.map((c) => (
                  <th key={c.key}
                    onClick={() => clickHeader(c.key)}
                    className={clsx('cursor-pointer select-none whitespace-nowrap px-3 py-2 hover:text-t1',
                      c.type !== 'text' && 'text-right')}>
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {d.sort === c.key && (d.dir === 'desc'
                        ? <ArrowDown className="size-3" /> : <ArrowUp className="size-3" />)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Totals above the rows, where Google puts them — it is the
                  number people look for first and scrolling to find it on a
                  hundred-row table is the thing that makes a report annoying. */}
              <tr className="border-t border-bd bg-bg3/60 font-medium">
                {d.columns.map((c, i) => (
                  <td key={c.key} className={clsx('whitespace-nowrap px-3 py-1.5', c.type !== 'text' && 'num text-right')}>
                    {i === 0 ? 'Total' : c.total ? fmt(d.totals[c.key], c.type, currency) : ''}
                  </td>
                ))}
              </tr>
              {d.rows.map((row, n) => (
                <tr key={n} className="border-t border-bd hover:bg-hover">
                  {d.columns.map((c) => {
                    const raw = row[c.key];
                    const text = c.key === 'hour' ? hourLabel(raw) : fmt(raw, c.type, currency);
                    return (
                      <td key={c.key}
                        title={c.wide ? String(raw ?? '') : undefined}
                        className={clsx('px-3 py-1.5',
                          c.type !== 'text' ? 'num whitespace-nowrap text-right' : 'max-w-xs truncate')}>
                        {text}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
